import React, { useState, useEffect } from 'react';
import {
  Page,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Select,
  Spinner,
  Icon,
  Box,
  Divider,
  Link,
  SkeletonDisplayText,
  SkeletonBodyText,
  Checkbox,
  Banner,
  Tooltip,
} from '@shopify/polaris';
import {
  TickMinor,
  KeyMajor,
  ColumnWithTextMajor,
  RefreshMajor,
} from '@shopify/polaris-icons';
import { redirectToAuth, fetchWithSessionToken } from '../lib/appBridge';
import { useNavigate } from 'react-router-dom';
import { trackAnalyticsEvent } from '../lib/analytics';

interface OnboardingPageProps {
  shop: string;
  host: string;
  onFinish: () => void;
}

type OnboardingStage = 'CHECK' | 'CONNECT' | 'MAP' | 'SYNC';

const OnboardingPage: React.FC<OnboardingPageProps> = ({ shop, host, onFinish }) => {
  const navigate = useNavigate();
  const [stage, setStage] = useState<OnboardingStage>('CHECK');
  const [isCheckingConnection, setIsCheckingConnection] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  // Mapping States
  const [cmsCollection, setCmsCollection] = useState('');
  const [titleField, setTitleField] = useState('');
  const [slugField, setSlugField] = useState('');
  const [thumbnailField, setThumbnailField] = useState('');
  const [contentField, setContentField] = useState('');
  const [metaDescField, setMetaDescField] = useState('');
  const [keywordField, setKeywordField] = useState('');
  const [isSavingMapping, setIsSavingMapping] = useState(false);
  const [mappingError, setMappingError] = useState<string | null>(null);

  // Shopify Blogs (CMS) for mapping
  const [blogs, setBlogs] = useState<{ id: string; title: string; handle: string }[]>([]);
  const [isLoadingBlogs, setIsLoadingBlogs] = useState(false);
  const [blogsError, setBlogsError] = useState<{ message: string; code?: string; reauthorizeUrl?: string } | null>(null);

  // Sync States (products)
  const [isSyncing, setIsSyncing] = useState(false);
  const [productSyncEnabled, setProductSyncEnabled] = useState(false);
  const [productSyncError, setProductSyncError] = useState<string | null>(null);
  const [productSyncCount, setProductSyncCount] = useState<number | null>(null);

  const normalizeShopDomain = (rawShop: string) => {
    const cleaned = rawShop.trim().toLowerCase().replace(/^https?:\/\//, '');
    const withoutQuery = cleaned.split('?')[0];
    const adminStoreMatch = withoutQuery.match(/admin\.shopify\.com\/store\/([^/]+)/);
    if (adminStoreMatch?.[1]) {
      return `${adminStoreMatch[1]}.myshopify.com`;
    }
    const hostOnly = withoutQuery.split('/')[0];
    if (hostOnly.includes('.myshopify.com')) {
      return hostOnly;
    }
    if (!hostOnly.includes('.')) {
      return `${hostOnly}.myshopify.com`;
    }
    return hostOnly;
  };

  const shopDomain = normalizeShopDomain(shop);
  const shopDisplayName = (() => {
    const slug = shopDomain.replace(/\.myshopify\.com$/i, '').trim();
    if (!slug) return 'this Shop';
    return slug
      .split('-')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  })();

  useEffect(() => {
    if (!shopDomain) return;
    trackAnalyticsEvent('onboarding_step_viewed', {
      route: '/onboarding',
      shopDomain,
      stage,
    });
  }, [shopDomain, stage]);

  const checkNativeConnection = async () => {
    if (!shopDomain || !host) return;
    try {
      setIsCheckingConnection(true);
      setConnectionError(null);
      const params = new URLSearchParams({ shop: shopDomain });
      if (host) {
        params.set('host', host);
      }
      const res = await fetchWithSessionToken(host, `/api/native-status?${params.toString()}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error || 'Failed to check connection status');
      }
      if (data?.connected) {
        trackAnalyticsEvent('genseo_connection_completed', {
          route: '/onboarding',
          shopDomain,
        });
        setProductSyncEnabled(Boolean(data?.productSyncEnabled));
        setStage('MAP');
        return;
      }

      const onboardingComplete = data?.projectOnboardingComplete === true;

      if (data?.hasSubscription) {
        if (onboardingComplete) {
          setStage('CONNECT');
          return;
        }

        navigate('/onboarding-info', { replace: true });
        return;
      }

      navigate('/pricing', { replace: true });
    } catch (error: any) {
      console.error('Failed to check native connection:', error);
      setConnectionError(error?.message || 'Failed to check connection status');
      navigate('/pricing', { replace: true });
    } finally {
      setIsCheckingConnection(false);
    }
  };

  useEffect(() => {
    checkNativeConnection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopDomain, host]);

  // Sobald wir im MAP-Step sind, Blogs aus Shopify laden
  useEffect(() => {
    if (stage !== 'MAP') return;
    if (!shop) return;

    const loadBlogs = async () => {
      try {
        setIsLoadingBlogs(true);
        setBlogsError(null);

        const params = new URLSearchParams({ shop: shopDomain });
        if (host) {
          params.set('host', host);
        }
        const res = await fetchWithSessionToken(host, `/api/blog-posts?${params.toString()}`);
        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          const err: any = new Error(data?.error || 'Failed to load blogs from Shopify');
          err.code = data?.code;
          err.reauthorizeUrl = data?.reauthorizeUrl;
          throw err;
        }

        let parsedBlogs: { id: string; title: string; handle: string }[] = [];

        // Neues Backend-Format: { blogs: [ {id,title,handle}, ... ] }
        if (Array.isArray((data as any)?.blogs)) {
          parsedBlogs = (data as any).blogs.map((b: any) => ({
            id: b.id,
            title: b.title,
            handle: b.handle,
          }));
        } else {
          // Fallback auf altes Format mit edges
          const rawBlogs = data?.data?.blogs?.edges || data?.blogs?.edges || [];
          parsedBlogs =
            rawBlogs.map((edge: any) => edge.node).map((b: any) => ({
              id: b.id,
              title: b.title,
              handle: b.handle,
            })) || [];
        }

        setBlogs(parsedBlogs);

        // Falls nur ein Blog vorhanden ist, optional vorauswählen
        if (parsedBlogs.length === 1 && !cmsCollection) {
          setCmsCollection(parsedBlogs[0].id);
        }
      } catch (error: any) {
        console.error('Failed to load Shopify blogs:', error);
        // Fehler explizit anzeigen, statt wie "keine Blogs" zu behandeln
        setBlogs([]);
        setBlogsError({
          message: error?.message || 'Failed to load blogs from Shopify',
          code: error?.code,
          reauthorizeUrl: error?.reauthorizeUrl,
        });
      } finally {
        setIsLoadingBlogs(false);
      }
    };

    loadBlogs();
  }, [stage, shop, host, cmsCollection, shopDomain]);

  // Wenn ein Blog gewählt ist, Mapping-Felder automatisch setzen (es gibt nur eine Option)
  useEffect(() => {
    if (!cmsCollection) return;

    if (!titleField) setTitleField('title');
    if (!slugField) setSlugField('handle');
    if (!thumbnailField) setThumbnailField('image');
    if (!contentField) setContentField('content');
    if (!metaDescField) setMetaDescField('excerpt');
    if (!keywordField) setKeywordField('tags');
  }, [cmsCollection, titleField, slugField, thumbnailField, contentField, metaDescField, keywordField]);

  const goToSync = async () => {
    if (!cmsCollection) return;
    try {
      setIsSavingMapping(true);
      setMappingError(null);

      const params = new URLSearchParams({ shop: shopDomain });
      if (host) {
        params.set('host', host);
      }

      const fieldMap = {
        title: titleField,
        slug: slugField,
        thumbnail: thumbnailField,
        content: contentField,
        meta_description: metaDescField,
        excerpt: metaDescField,
        keyword: keywordField,
      };

      const res = await fetchWithSessionToken(host, `/api/native-mapping?${params.toString()}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          blogId: cmsCollection,
          fieldMap,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error || 'Failed to save Shopify mapping');
      }

      try {
        window.localStorage.setItem(`genseo_shopify_blog_id:${shopDomain}`, cmsCollection);
      } catch {}

      trackAnalyticsEvent('mapping_saved', {
        route: '/onboarding',
        shopDomain,
        source: 'onboarding',
      });
      setStage('SYNC');
    } catch (error: any) {
      console.error('Failed to save Shopify mapping:', error);
      setMappingError(error?.message || 'Failed to save Shopify mapping');
    } finally {
      setIsSavingMapping(false);
    }
  };
  const handleFinishSync = () => onFinish();

  const startSync = async () => {
    if (!shop) {
      return;
    }

    try {
      setIsSyncing(true);
      setProductSyncError(null);
      setProductSyncCount(null);
      trackAnalyticsEvent('product_sync_preference_changed', {
        route: '/onboarding',
        shopDomain,
        productSyncEnabled,
      });
      const settingsRes = await fetchWithSessionToken(host, '/api/product-sync-settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productSyncEnabled }),
      });
      const settingsData = await settingsRes.json().catch(() => ({}));
      if (!settingsRes.ok) {
        throw new Error(settingsData?.error || 'Failed to save product sync settings');
      }

      if (!productSyncEnabled) {
        handleFinishSync();
        return;
      }

      trackAnalyticsEvent('product_sync_started', {
        route: '/onboarding',
        shopDomain,
        source: 'onboarding',
      });
      const res = await fetchWithSessionToken(host, '/api/sync-products', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shop }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.error || 'Product sync failed');
      }
      setProductSyncCount(Number(data?.synced || 0));
      trackAnalyticsEvent('product_sync_completed', {
        route: '/onboarding',
        shopDomain,
        source: 'onboarding',
      });
      handleFinishSync();
    } catch (error) {
      trackAnalyticsEvent('product_sync_failed', {
        route: '/onboarding',
        shopDomain,
        source: 'onboarding',
      });
      console.error('Product sync error:', error);
      setProductSyncError((error as any)?.message || 'Product sync failed');
    } finally {
      setIsSyncing(false);
    }
  };

  const stepperStages: OnboardingStage[] = ['CONNECT', 'MAP', 'SYNC'];

  // Map CHECK to the first step for display
  const getStepperIndex = () => {
    if (stage === 'CHECK') return 0;
    return stepperStages.indexOf(stage);
  };

  const currentStepIndex = getStepperIndex();

  const renderStepper = () => {
    const steps = [
      { label: 'Connect', icon: KeyMajor },
      { label: 'Map', icon: ColumnWithTextMajor },
      { label: 'Sync', icon: RefreshMajor },
    ];

    return (
      <Box paddingBlock="600">
        <InlineStack align="center" gap="400" blockAlign="center">
          {steps.map((step, index) => {
            const isCompleted = index < currentStepIndex;
            const isActive = index === currentStepIndex;

            return (
              <React.Fragment key={step.label}>
                <InlineStack gap="200" blockAlign="center">
                  <div
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: isCompleted
                        ? 'var(--p-color-bg-fill-inverse)'
                        : 'var(--p-color-bg-surface-secondary)',
                      border: isActive
                        ? '2px solid var(--p-color-border-info-focus)'
                        : '1px solid var(--p-color-border-secondary)',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <div style={{ color: 'white' }}>
                      <Icon
                        source={isCompleted ? TickMinor : step.icon}
                        tone={isCompleted ? undefined : (isActive ? 'base' : 'subdued')}
                      />
                    </div>
                  </div>
                  <Text
                    variant="bodyMd"
                    as="span"
                    fontWeight={isActive ? 'bold' : 'regular'}
                    tone={isActive ? 'base' : 'subdued'}
                  >
                    {step.label}
                  </Text>
                </InlineStack>
                {index < steps.length - 1 && (
                  <div
                    style={{
                      width: '32px',
                      height: '2px',
                      background: isCompleted
                        ? 'linear-gradient(90deg, #454f5b 0%, #8c9196 100%)'
                        : 'var(--p-color-border-secondary)',
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </InlineStack>
      </Box>
    );
  };

  const renderChecking = () => (
    <div
      style={{
        minHeight: '260px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '16px',
      }}
    >
      <div style={{ width: '100%', maxWidth: '420px', marginTop: '1rem' }}>
        {isCheckingConnection && (
          <InlineStack align="center">
            <Spinner size="small" accessibilityLabel="Checking connection" />
          </InlineStack>
        )}
        <BlockStack gap="300">
          <SkeletonDisplayText size="large" />
          <SkeletonBodyText lines={2} />
        </BlockStack>
      </div>
    </div>
  );

  const renderConnect = () => (
    <BlockStack gap="600">
      <BlockStack gap="200">
        <Text variant="headingLg" as="h2">
          Connect {shopDisplayName} with Genseo
        </Text>
        <Text as="p" tone="subdued">
          We could not find an active Genseo subscription for this shop. Connect your store in
          Genseo to continue, then return here.
        </Text>
      </BlockStack>

      <BlockStack gap="400">
        <Card>
          <Box padding="400">
            <BlockStack gap="300">
              <Text as="p" tone="subdued" variant="bodySm">
                Manage your subscription on the Plans page.
              </Text>
              <Button variant="secondary" onClick={() => navigate('/pricing')}>
                Go to Plans
              </Button>
            </BlockStack>
          </Box>
        </Card>

        {connectionError && (
          <Text as="p" tone="critical" variant="bodySm">
            {connectionError}
          </Text>
        )}
      </BlockStack>
    </BlockStack>
  );

  const renderMap = () => (
    <BlockStack gap="600">
      <BlockStack gap="200">
        <Text variant="headingLg" as="h2">
          Connect the Fields
        </Text>
        <Text as="p" tone="subdued">
          Seamlessly integrate Genseo into your Shopify project. Choose where your content should go.
        </Text>
      </BlockStack>

      <BlockStack gap="400">
        {/* Blog Auswahl aus echtem Shopify-CMS */}
        <Select
          label="Choose Blog (CMS Collection)"
          options={[
            { label: 'Select Blog', value: '' },
            ...blogs.map((blog) => ({
              label: blog.title || blog.handle || blog.id,
              value: blog.id,
            })),
          ]}
          onChange={setCmsCollection}
          value={cmsCollection}
          disabled={isLoadingBlogs || blogs.length === 0}
        />
        {isLoadingBlogs && !blogsError && (
          <InlineStack align="start" gap="200" blockAlign="center">
            <Spinner size="small" accessibilityLabel="Loading blogs" />
            <Text as="span" tone="subdued" variant="bodySm">
              Loading blogs from your Shopify store…
            </Text>
          </InlineStack>
        )}
        {!isLoadingBlogs && blogsError && (
          <BlockStack gap="200">
            <Text as="p" tone="critical" variant="bodySm">
              {blogsError.code === 'SESSION_MISSING'
                ? 'Your Shopify session is missing. Please reopen the app from the Shopify admin so we can access your store.'
                : blogsError.code === 'INSUFFICIENT_SCOPE'
                  ? 'The app is missing required permissions to read blogs. Please reinstall the app with content read access, then reopen it.'
                  : blogsError.message || 'We could not load blogs from Shopify. Please try again or reopen the app from the Shopify admin.'}
            </Text>
            <InlineStack gap="200">
              {blogsError.reauthorizeUrl ? (
                <Button
                  variant="primary"
                  onClick={() => {
                    const redirected = redirectToAuth(host, blogsError.reauthorizeUrl as string);
                    if (!redirected) {
                      window.location.href = blogsError.reauthorizeUrl as string;
                    }
                  }}
                >
                  Reconnect Shopify
                </Button>
              ) : (
                <Button variant="secondary" onClick={() => window.location.reload()}>
                  Reload App
                </Button>
              )}
              <Button
                variant="plain"
                onClick={() => {
                  trackAnalyticsEvent('external_dashboard_docs_links_opened', {
                    route: '/onboarding',
                    shopDomain,
                    destination: 'shopify_apps',
                  });
                  window.open(
                    `https://${shopDomain}/admin/apps`,
                    '_blank'
                  );
                }}
              >
                Open Shopify Apps
              </Button>
            </InlineStack>
          </BlockStack>
        )}
        {!isLoadingBlogs && !blogsError && blogs.length === 0 && (
          <BlockStack gap="200">
            <Text as="p" tone="subdued" variant="bodySm">
              No blogs were found in your Shopify store. Please create a blog first in your Shopify admin, then return here to complete the mapping.
            </Text>
            <Button
              variant="secondary"
              onClick={() => {
                trackAnalyticsEvent('external_dashboard_docs_links_opened', {
                  route: '/onboarding',
                  shopDomain,
                  destination: 'shopify_blogs',
                });
                window.open(
                  `https://${shopDomain}/admin/blogs`,
                  '_blank'
                );
              }}
            >
              Open Shopify Blogs
            </Button>
          </BlockStack>
        )}
        <Divider />
        <Select
          label={
            <InlineStack gap="050">
              <Text as="span">Title Field</Text>
              <Text as="span" tone="critical">*</Text>
            </InlineStack>
          }
          options={[{ label: 'Select Title Field', value: '' }, { label: 'Shopify Title', value: 'title' }]}
          onChange={setTitleField}
          value={titleField}
          disabled={!cmsCollection}
        />
        <Select
          label={
            <InlineStack gap="050">
              <Text as="span">Slug Field</Text>
              <Text as="span" tone="critical">*</Text>
            </InlineStack>
          }
          options={[{ label: 'Select Slug Field', value: '' }, { label: 'Shopify Handle', value: 'handle' }]}
          onChange={setSlugField}
          value={slugField}
          disabled={!cmsCollection}
        />
        <Select
          label="Thumbnail Field"
          options={[{ label: 'Select Image Field', value: '' }, { label: 'Featured Image', value: 'image' }]}
          onChange={setThumbnailField}
          value={thumbnailField}
          disabled={!cmsCollection}
        />
        <Select
          label={
            <InlineStack gap="050">
              <Text as="span">Blog post Field</Text>
              <Text as="span" tone="critical">*</Text>
            </InlineStack>
          }
          options={[{ label: 'Select Blog post Field', value: '' }, { label: 'Shopify Content', value: 'content' }]}
          onChange={setContentField}
          value={contentField}
          disabled={!cmsCollection}
        />
        <Select
          label="Meta description Field"
          options={[{ label: 'Select Meta Description Field', value: '' }, { label: 'Standard Excerpt', value: 'excerpt' }]}
          onChange={setMetaDescField}
          value={metaDescField}
          disabled={!cmsCollection}
        />
        <Select
          label="Keyword Field"
          options={[{ label: 'Select Keyword Field', value: '' }, { label: 'Tags', value: 'tags' }]}
          onChange={setKeywordField}
          value={keywordField}
          disabled={!cmsCollection}
        />
        <Button
          variant="primary"
          size="large"
          onClick={goToSync}
          fullWidth
          loading={isSavingMapping}
          disabled={!cmsCollection || !titleField || !slugField || !contentField || isSavingMapping}
        >
          Mapping save & next
        </Button>
        {mappingError ? (
          <Text tone="critical" as="p">
            {mappingError}
          </Text>
        ) : null}
      </BlockStack>
    </BlockStack>
  );

  const renderSync = () => (
    <BlockStack gap="600">
      <BlockStack gap="200">
        <Text variant="headingLg" as="h2">
          Sync your Products
        </Text>
        <Text as="p" tone="subdued">
          Synchronize your Shopify products with Genseo to keep your content up to date.
        </Text>
      </BlockStack>

      <BlockStack gap="400">
        <Card background="bg-surface-secondary">
          <Box padding="400">
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <BlockStack gap="100">
                  <Checkbox
                    label={
                      <span>
                        <Tooltip content="You can disable this at any time or remove products from sync in Genseo under Profile > Products.">
                          <span className="product-sync-tooltip-trigger">
                            Sync Shopify products
                          </span>
                        </Tooltip>{' '}
                        with Genseo
                      </span>
                    }
                    checked={productSyncEnabled}
                    onChange={setProductSyncEnabled}
                    disabled={isSyncing}
                  />
                  <Text variant="bodySm" as="p" tone="subdued">
                    Import your catalog now and keep future Shopify product changes synced automatically.
                  </Text>
                </BlockStack>
                <Button onClick={startSync} loading={isSyncing} disabled={isSyncing}>
                  {productSyncEnabled ? 'Save & sync products' : 'Save & continue'}
                </Button>
              </InlineStack>
            </BlockStack>
          </Box>
        </Card>

        {productSyncError && (
          <Banner tone="critical">
            <p>{productSyncError}</p>
          </Banner>
        )}

        {productSyncCount !== null && (
          <Banner tone="success">
            <p>{productSyncCount} products synced with Genseo.</p>
          </Banner>
        )}

        <Text as="p" variant="bodySm" tone="subdued" alignment="center">
          You can always manage your sync settings in the Genseo Dashboard later.
        </Text>

        <Box paddingBlock="200">
          <Button variant="plain" onClick={handleFinishSync} fullWidth disabled={isSyncing}>
            Skip for now
          </Button>
        </Box>

      </BlockStack>
    </BlockStack>
  );

  if (stage === 'CHECK') {
    return (
      <div
        style={{
          background: '#f6f6f7',
          minHeight: '100vh',
          width: '100%',
          paddingBottom: '80px',
          position: 'relative',
        }}
      >
        <Page narrowWidth>
          <Card>
            <div style={{ marginTop: '1rem' }}>
              <Box padding="1000">
                {renderChecking()}
              </Box>
            </div>
          </Card>
        </Page>

        {/* Fixed Footer Link */}
        <div
          style={{
            position: 'fixed',
            bottom: '20px',
            left: 0,
            right: 0,
            textAlign: 'center',
            pointerEvents: 'none',
            zIndex: 100,
          }}
        >
          <div style={{ pointerEvents: 'auto', display: 'inline-block' }}>
            <Link
              url="https://docs.genseo.co/integrations/shopify"
              external
              onClick={() =>
                trackAnalyticsEvent('external_dashboard_docs_links_opened', {
                  route: '/onboarding',
                  shopDomain,
                  destination: 'shopify_docs',
                })
              }
            >
              <Text as="span" variant="bodySm" tone="subdued">
                Full Shopify Setup Guide
              </Text>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        background: '#f6f6f7',
        minHeight: '100vh',
        width: '100%',
        paddingBottom: '80px', // Extra padding for footer
        position: 'relative'
      }}
    >
      <Page narrowWidth>
        <BlockStack gap="400">
          {renderStepper()}
          <Card>
            <Box padding="1000">
              {stage === 'CONNECT' && renderConnect()}
              {stage === 'MAP' && renderMap()}
              {stage === 'SYNC' && renderSync()}
            </Box>
          </Card>
        </BlockStack>
      </Page>

      {/* Sync modal removed */}

      {/* Fixed Footer Link */}
      <div style={{
        position: 'fixed',
        bottom: '20px',
        left: 0,
        right: 0,
        textAlign: 'center',
        pointerEvents: 'none', // Allow clicks through container
        zIndex: 100
      }}>
        <div style={{ pointerEvents: 'auto', display: 'inline-block' }}>
          <Link
            url="https://docs.genseo.co/integrations/shopify"
            external
            onClick={() =>
              trackAnalyticsEvent('external_dashboard_docs_links_opened', {
                route: '/onboarding',
                shopDomain,
                destination: 'shopify_docs',
              })
            }
          >
            <Text as="span" variant="bodySm" tone="subdued">Full Shopify Setup Guide</Text>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;
